import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Form, InputGroup } from "react-bootstrap";
import {
  IconPackageExport,
  IconAlertTriangle,
  IconTrendingUp,
  IconPackage,
  IconSearch,
} from "@tabler/icons-react";
import AsyncSelect from "react-select/async";
import { getRequest } from "services/api";

const InventoryStats = ({
  stats,
  onStatusFilter,
  searchTerm,
  onSearchChange,
  filterOptions,
  categories,
  onCategoryFilter,
  getCategoryData,
  onWarehouseFilter,
  activeFilter,
  onActiveFilter,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [allCategories, setAllCategories] = useState([]);

  // Fetch all categories for default options
  useEffect(() => {
    if (Array.isArray(categories)) {
      const categoryOptions = categories.map((category) => ({
        value: category.id,
        label: category.name,
      }));
      setAllCategories(categoryOptions);
    }
  }, [categories]);

  // Load options function for AsyncSelect
  const loadCategoryOptions = async (inputValue) => {
    // If we have categories data, filter locally first
    if (Array.isArray(categories)) {
      const filtered = categories
        .filter((category) =>
          category.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((category) => ({
          value: category.id,
          label: category.name,
        }));
      return filtered;
    }

    // Fallback to API call
    try {
      const response = await getRequest(
        `/inventory-category?search=${inputValue}&limit=1000`
      );
      if (response.success) {
        return (response.data || []).map((category) => ({
          value: category.id,
          label: category.name,
        }));
      }
      return [];
    } catch (error) {
      console.error("Failed to load category options:", error);
      return [];
    }
  };

  // Get current selected category
  const getCurrentCategory = () => {
    if (!filterOptions.category_id) return null;

    // First try to find from categories
    if (Array.isArray(categories)) {
      const category = categories.find(
        (cat) => cat.id === filterOptions.category_id
      );
      if (category) {
        return {
          value: category.id,
          label: category.name,
        };
      }
    }

    // Fallback to allCategories state
    if (allCategories.length > 0) {
      const category = allCategories.find(
        (cat) => cat.value === filterOptions.category_id
      );
      if (category) {
        return category;
      }
    }

    // If still not found, return null (don't show "Uncategorized")
    return null;
  };

  const statusOptions = [
    {
      key: "all",
      label: "Semua",
      color: "#6b7280",
      bgColor: "#f3f4f6",
      icon: IconPackage,
    },
    {
      key: "low",
      label: "Stok Rendah",
      color: "#d97706",
      bgColor: "#fef3c7",
      icon: IconAlertTriangle,
    },
    {
      key: "out",
      label: "Habis",
      color: "#dc2626",
      bgColor: "#fee2e2",
      icon: IconTrendingUp,
    },
  ];

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    if (onStatusFilter) {
      onStatusFilter(status);
    }
  };

  const statCards = [
    {
      title: "Total Item",
      value: stats.totalItems,
      icon: IconPackageExport,
      variant: "outline-primary",
      textClass: "text-primary",
    },
    // {
    //   title: "Item Stok Rendah",
    //   value: stats.lowStockCount,
    //   icon: IconAlertTriangle,
    //   variant: "outline-warning",
    //   textClass: "text-warning",
    // },
    // {
    //   title: "Item Habis",
    //   value: stats.outOfStockCount || 0,
    //   icon: IconTrendingUp,
    //   variant: "outline-danger",
    //   textClass: "text-danger",
    // },
    {
      title: "Office Low Stock",
      value: stats.officeLowStock || 0,
      icon: IconAlertTriangle,
      variant: "outline-warning",
      textClass: "text-warning",
    },
    {
      title: "Office Out of Stock",
      value: stats.officeOutOfStock || 0,
      icon: IconTrendingUp,
      variant: "outline-danger",
      textClass: "text-danger",
    },
    {
      title: "Mechanic Low Stock",
      value: stats.mechanicLowStock || 0,
      icon: IconAlertTriangle,
      variant: "outline-warning",
      textClass: "text-warning",
    },
    {
      title: "Mechanic Out of Stock",
      value: stats.mechanicOutOfStock || 0,
      icon: IconTrendingUp,
      variant: "outline-danger",
      textClass: "text-danger",
    },
  ];

  // if (canSeePrice) {
  //   statCards.splice(1, 0, {
  //     title: "Total Inventory Value",
  //     value: formatPrice(stats.totalValue || 0),
  //     icon: IconTrendingUp,
  //     variant: "outline-success",
  //     textClass: "text-success",
  //   });
  // }
  return (
    <div style={{ marginBottom: "32px" }}>
      <Row className="g-2">
        {statCards.map((card, index) => (
          <Col md={4} lg={3} xl={3} key={index}>
            <Card
              variant={card.variant}
              className={`card ${card.textClass} shadow-sm`}
            >
              <Card.Body className="p-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-fill">
                    <div className="text-uppercase fw-medium fs-6 opacity-75 mb-1">
                      {card.title}
                    </div>
                    <div className="display-6 fw-bold mb-0">{card.value}</div>
                  </div>
                  <div className="avatar avatar-xs bg-light ms-3">
                    <card.icon className="icon" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}

        {/* Status Filter Card */}
        <Col md={12}>
          <Card className="card shadow-sm">
            <Card.Body className="p-3">
              {/* Search and Filter Section */}
              <div className="mb-4">
                <h6 className="mb-3 text-muted">Search & Filter</h6>
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: "250px" }}>
                    <InputGroup>
                      <InputGroup.Text>
                        <IconSearch size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Cari item berdasarkan nama ..."
                        value={searchTerm}
                        onChange={onSearchChange}
                      />
                    </InputGroup>
                  </div>

                  <div style={{ minWidth: "200px" }}>
                    <AsyncSelect classNamePrefix="react-select" className="react-select-container"
                      loadOptions={loadCategoryOptions}
                      value={getCurrentCategory()}
                      onChange={(selected) => {
                        onCategoryFilter(selected ? selected.value : "");
                      }}
                      isSearchable
                      placeholder="Pilih kategori..."
                      isClearable
                      defaultOptions={allCategories}
                      cacheOptions={false}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderColor: "#dee2e6",
                          "&:hover": {
                            borderColor: "#adb5bd",
                          },
                        }),
                      }}
                    />
                  </div>

                  <div style={{ minWidth: "150px" }}>
                    <Form.Select
                      value={activeFilter === "" || activeFilter === undefined ? "all" : activeFilter}
                      onChange={(e) => {
                        const val = e.target.value;
                        onActiveFilter(val === "all" ? "" : val);
                      }}
                    >
                      <option value="all">Semua Status Aktif</option>
                      <option value="1">Aktif</option>
                      <option value="0">Tidak Aktif</option>
                    </Form.Select>
                  </div>

                  {/* <div style={{ minWidth: "150px" }}>
                    <Form.Select
                      value={filterOptions.warehouse || "all"}
                      onChange={(e) => onWarehouseFilter(e.target.value)}
                    >
                      <option value="all">All Warehouses</option>
                      <option value="office">Office Only</option>
                      <option value="mechanic">Mechanic Only</option>
                    </Form.Select>
                  </div> */}

                  {/* <div style={{ minWidth: "150px" }}>
                    <Form.Check
                      type="checkbox"
                      id="lowStockFilter"
                      label="Low Stock Only"
                      checked={filterOptions.showLowStock || false}
                      onChange={(e) => onLowStockFilter(e.target.checked)}
                      style={{ marginTop: "8px" }}
                    />
                  </div> */}

                  {/* <h6 className="mb-3 text-muted">Filter by Status</h6> */}
                  <div className="d-flex flex-wrap gap-2">
                    {statusOptions.map((status) => {
                      const IconComponent = status.icon;
                      const isSelected = selectedStatus === status.key;
                      return (
                        <Button
                          key={status.key}
                          variant={isSelected ? "primary" : "outline-secondary"}
                          size="xl"
                          onClick={() => handleStatusFilter(status.key)}
                          className={`d-flex align-items-center gap-1 ${
                            isSelected ? "shadow-sm" : ""
                          }`}
                        >
                          <IconComponent size={24} />
                          {status.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InventoryStats;
